import React from "react";
import { FaUser, FaEnvelope, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import {
  useMatchUser,
  UserInfoCard,
  UserFetchSection,
  ConfirmNotificationModal,
} from "../../components/MatchUser";

export default function MatchUsers() {
  const {
    senderEmail,
    setSenderEmail,
    receiverEmail,
    setReceiverEmail,
    senderUser,
    receiverUser,
    fetchingSender,
    fetchingReceiver,
    sending,
    result,
    showConfirmModal,
    setShowConfirmModal,
    handleFetchSender,
    handleFetchReceiver,
    handleOpenConfirmModal,
    handleSendNotification,
    handleReset,
  } = useMatchUser();

  return (
    <div className="bg-white rounded-xl w-full min-h-[calc(100vh-120px)] shadow-xl p-3 sm:p-4 md:p-6 overflow-x-hidden">
      <div className="max-w-7xl mx-auto min-w-0">
        <header className="text-center mb-4 sm:mb-6 md:mb-8 px-2">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-red-600 mb-2">
            Send Notification Between Users
          </h1>
          <p className="text-gray-600 text-sm sm:text-base md:text-lg">
            Fetch users by email and send matched profile notifications
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mb-4 sm:mb-5 md:mb-6">
          <UserFetchSection
            title="Sender User"
            icon={FaUser}
            label="Sender Email Address"
            value={senderEmail}
            onChange={setSenderEmail}
            onFetch={handleFetchSender}
            loading={fetchingSender}
            placeholder="e.g., sender@example.com"
          />
          <UserFetchSection
            title="Receiver User"
            icon={FaEnvelope}
            label="Receiver Email Address"
            value={receiverEmail}
            onChange={setReceiverEmail}
            onFetch={handleFetchReceiver}
            loading={fetchingReceiver}
            placeholder="e.g., receiver@example.com"
          />
        </div>

        {(senderUser || receiverUser) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mb-4 sm:mb-5 md:mb-6 min-w-0">
            {senderUser && (
              <UserInfoCard
                user={senderUser}
                title="Sender User"
                type="sender"
              />
            )}
            {receiverUser && (
              <UserInfoCard
                user={receiverUser}
                title="Receiver User"
                type="receiver"
              />
            )}
          </div>
        )}

        {senderUser && receiverUser && (
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 pt-4 sm:pt-5 md:pt-6 px-2">
            <button
              type="button"
              onClick={handleOpenConfirmModal}
              disabled={
                sending ||
                senderUser.id === receiverUser.id
              }
              className={`w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg sm:rounded-xl font-bold text-base sm:text-lg shadow-lg transition-all flex items-center justify-center gap-2 ${
                sending || senderUser.id === receiverUser.id
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              }`}>
              <FaEnvelope />
              <span className="text-sm sm:text-base">Send Notification</span>
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={sending || fetchingSender || fetchingReceiver}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gray-500 text-white rounded-lg sm:rounded-xl font-semibold hover:bg-gray-600 transition shadow-lg text-sm sm:text-base">
              Reset
            </button>
          </div>
        )}

        {showConfirmModal && (
          <ConfirmNotificationModal
            senderUser={senderUser}
            receiverUser={receiverUser}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={handleSendNotification}
            sending={sending}
          />
        )}

        {result && (
          <div className="mt-4 sm:mt-5 md:mt-6 px-2 min-w-0">
            <div
              className={`p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl border-2 shadow-lg ${
                result.success
                  ? "bg-gradient-to-br from-green-50 to-green-100 border-green-400"
                  : "bg-gradient-to-br from-red-50 to-red-100 border-red-400"
              }`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                {result.success ? (
                  <FaCheckCircle className="text-2xl sm:text-3xl text-green-600 flex-shrink-0" />
                ) : (
                  <FaTimesCircle className="text-2xl sm:text-3xl text-red-600 flex-shrink-0" />
                )}
                <h3
                  className={`font-bold text-base sm:text-lg md:text-xl ${
                    result.success ? "text-green-700" : "text-red-700"
                  }`}>
                  {result.success
                    ? "Notification Sent Successfully!"
                    : "Error Sending Notification"}
                </h3>
              </div>
              <p
                className={`text-sm sm:text-base md:text-lg break-words ${
                  result.success ? "text-green-800" : "text-red-800"
                }`}>
                {result.message}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
